import {
  BlobArray,
  type BlobLike,
  BlobToUint8,
  FileCache,
} from "@pdfking/compatibility"
import {
  type AsyncSequence,
  type Bytes,
  CopyIterable,
  GuidToString,
  IterableToArray,
  MapAsync,
  ReduceAsync,
  SafeIterable,
  UInt8Hash,
  UnixTime,
} from "@pdfking/tfunc"

import type { PdfEngine } from "../types/engine/PdfEngine"
import type { GeneratedFile } from "../types/files/cached/GeneratedFile"
import type { ContentType } from "../types/files/ContentType"
import type { TrackingCallback } from "../types/tasks/tracking/TrackingCallback"

import { AutoThrow } from "../errors/AutoThrow"
import { VirtualFileType } from "../types/files/VirtualFileType"
import { TrackedTaskName } from "../types/tasks/tracking/TrackedTaskName"

export const GenerateFile = async (
  { config: { file_cache, tracking_chunk_size } }: PdfEngine,
  stream: AsyncSequence<BlobLike>,
  content_type: ContentType,
  emit: TrackingCallback
): Promise<GeneratedFile> => {
  const { error, stream: copy } = SafeIterable(stream)

  const [chunks, body_size, body_hash] = CopyIterable(copy, 3)

  const guid = UInt8Hash(MapAsync(BlobToUint8, body_hash)).then(GuidToString)
  const size = await ReduceAsync(
    (acc: Bytes, blob: BlobLike) => (acc + blob.size) as Bytes,
    body_size,
    0 as Bytes
  )

  const emitter = (done: 0 | 1 = 0) => {
    if (size > tracking_chunk_size) {
      emit({ done, name: TrackedTaskName.GENERATE_FILE, total: 1 })
    }
  }

  emitter()

  await AutoThrow(error)

  const modified = UnixTime()

  // Store in cache using the hash as part of the key
  await FileCache(file_cache).set(
    await guid,
    await IterableToArray(chunks).then(BlobArray)
  )

  emitter(1)

  return {
    enum: VirtualFileType.GENERATED_FILE,
    file_meta: {},
    guid: await guid,
    modified,
    size,
    type: content_type,
  }
}
